import { useRememberDocument, useDocumentWorkspace } from '@/contexts/DocumentWorkspace';
import { useIncomingDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Upload, EyeOff, Trash2,
  AlertTriangle, ShieldCheck, Loader2, Download, Eye,
  ChevronLeft, ChevronRight, Undo2, Redo2,
} from 'lucide-react';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, pdfIntakeErrorMessage } from '@/lib/pdfIntake';
import { formatFileSize } from '@/lib/pdfOperations';
import { saveViaWorker } from '@/lib/saveWorkerClient';
import { saveDocumentSnapshot } from '@/lib/documentSave';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import PageCanvas from '@/components/PageCanvas';
import { ProGate } from '@/components/ProBadge';
import { hasFeature } from '@/lib/license';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

// ─── constants ────────────────────────────────────────────────────────────────
const EDITOR_TARGET_W = 750;
const EDITOR_TARGET_H = 600;
const HANDLE_SIZE = 8;
const HANDLE_HIT = 10;
const MIN_RECT = 8;
const MAX_HISTORY = 50;

// ─── pure helpers ─────────────────────────────────────────────────────────────
let nextId = 1;
function genId() { return `redact-${nextId++}`; }

function getHandlePositions(rect) {
  const { x, y, w, h } = rect;
  return [
    { id: 'nw', x,         y         },
    { id: 'ne', x: x + w,  y         },
    { id: 'se', x: x + w,  y: y + h  },
    { id: 'sw', x,         y: y + h  },
  ];
}

function getHandleAt(mx, my, rect) {
  for (const h of getHandlePositions(rect)) {
    if (Math.abs(mx - h.x) <= HANDLE_HIT && Math.abs(my - h.y) <= HANDLE_HIT) return h.id;
  }
  return null;
}

const HANDLE_CURSORS = { nw: 'nwse-resize', ne: 'nesw-resize', se: 'nwse-resize', sw: 'nesw-resize' };

function applyResize(handle, startRect, dx, dy) {
  let { x, y, w, h } = startRect;
  if (handle === 'nw') { x += dx; y += dy; w -= dx; h -= dy; }
  else if (handle === 'ne') { y += dy; w += dx; h -= dy; }
  else if (handle === 'se') { w += dx; h += dy; }
  else if (handle === 'sw') { x += dx; w -= dx; h += dy; }
  if (w < 0) { x += w; w = -w; }
  if (h < 0) { y += h; h = -h; }
  return { x, y, w, h };
}

function isInsideRect(mx, my, rect) {
  return mx >= rect.x && mx <= rect.x + rect.w && my >= rect.y && my <= rect.y + rect.h;
}

// ─── component ────────────────────────────────────────────────────────────────
export default function Redact() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const workspace = useDocumentWorkspace();
  const { openFile, saveFile: saveFileDialog } = useFileDialog();

  // Pro feature gate — computed once per render; the actual short-circuit
  // render happens AFTER all hooks are declared (see render block below).
  // This keeps hook order stable across paid/unpaid transitions.
  const redactGated = !hasFeature('redact');

  const overlayRef = useRef(null);
  const dragRef = useRef(null);
  const scaleRef = useRef(1);

  // File state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Page navigation
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputVal, setPageInputVal] = useState('1');
  const [pageSize, setPageSize] = useState(null);
  const [scale, setScale] = useState(1);

  // Tools
  const [fillColor, setFillColor] = useState('black'); // 'black' | 'white'

  // Redaction marks — stored in PDF coordinate space (origin bottom-left)
  const [marks, setMarks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Undo / redo history
  const [history, setHistory] = useState([]);   // past mark snapshots
  const [future, setFuture] = useState([]);     // redo snapshots
  const marksRef = useRef(marks);
  marksRef.current = marks;

  // Push current marks onto history before a mutation
  const pushHistory = useCallback(() => {
    setHistory(prev => {
      const next = [...prev, marksRef.current];
      return next.length > MAX_HISTORY ? next.slice(next.length - MAX_HISTORY) : next;
    });
    setFuture([]); // clear redo on new action
  }, []);

  const undo = useCallback(() => {
    setHistory(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setFuture(f => [...f, marksRef.current]);
      setMarks(last);
      setSelectedId(null);
      return prev.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture(prev => {
      if (prev.length === 0) return prev;
      const next = prev[prev.length - 1];
      setHistory(h => [...h, marksRef.current]);
      setMarks(next);
      setSelectedId(null);
      return prev.slice(0, -1);
    });
  }, []);

  // Confirmation dialog
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');

  // Processing
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  // Result
  const [result, setResult] = useState(null);
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // ─── derived ───
  const pageDisplayW = pageSize ? pageSize.width * scale : 0;
  const pageDisplayH = pageSize ? pageSize.height * scale : 0;
  const currentMarks = marks.filter(m => m.pageNumber === currentPage);
  const totalMarkCount = marks.length;
  const pagesWithMarks = new Set(marks.map(m => m.pageNumber)).size;

  // ─── PDF coordinate helpers ───
  const displayToPdf = useCallback((dx, dy, dw, dh) => {
    const s = scaleRef.current;
    if (!pageSize) return { x: 0, y: 0, width: 0, height: 0 };
    const [x1, y1] = pageSize.viewport.convertToPdfPoint(dx / s, dy / s);
    const [x2, y2] = pageSize.viewport.convertToPdfPoint((dx + dw) / s, (dy + dh) / s);
    return {
      x: Math.min(x1, x2), y: Math.min(y1, y2),
      width: Math.abs(x2 - x1), height: Math.abs(y2 - y1),
    };
  }, [pageSize]);

  const pdfToDisplay = useCallback((mark) => {
    const s = scaleRef.current;
    if (!pageSize) return { x: 0, y: 0, w: 0, h: 0 };
    const [x1, y1, x2, y2] = pageSize.viewport.convertToViewportRectangle([mark.x, mark.y, mark.x + mark.width, mark.y + mark.height]);
    return {
      x: Math.min(x1, x2) * s, y: Math.min(y1, y2) * s,
      w: Math.abs(x2 - x1) * s, h: Math.abs(y2 - y1) * s,
    };
  }, [pageSize]);

  // ─── file loading ───
  const loadFile = useCallback(async (file) => {
    if (!file || file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return;
    if (marksRef.current.length && !result) {
      setError('Apply your redactions or clear the marks before opening another file.');
      return;
    }
    if (pdfDoc) pdfDoc.destroy();
    setError(null);
    setResult(null);
    setMarks([]);
    setHistory([]);
    setFuture([]);
    setSelectedId(null);
    setCurrentPage(1);
    setPageInputVal('1');
    setFileName(file.name);
    setFileSize(file.size);

    try {
      // Decrypt owner-password PDFs (lossless) / reject truly locked ones
      // before pdf-lib ever sees the bytes — see src/lib/pdfIntake.js.
      const buf = await readPdfLibBytes(file);
      setArrayBuffer(buf);
      const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
      setPdfDoc(doc);
      rememberDocument(file);
      setNumPages(doc.numPages);

      const page = await doc.getPage(1);
      const vp = page.getViewport({ scale: 1 });
      const ps = { width: vp.width, height: vp.height, viewport: vp };
      setPageSize(ps);

      const s = Math.min(EDITOR_TARGET_W / ps.width, EDITOR_TARGET_H / ps.height, 1.5);
      scaleRef.current = s;
      setScale(s);
    } catch (err) {
      setError(pdfIntakeErrorMessage(err, 'redact it'));
    }
  }, [pdfDoc, rememberDocument, result]);

  useIncomingDocument(loadFile);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setIsDragging(false); loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);
  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (result) {
      const file = new File([result.buffer], result.name, { type: 'application/pdf' });
      loadFile(file);
    }
  }, [openFile, loadFile]);

  // ─── page navigation ───
  const goToPage = useCallback(async (target) => {
    if (!pdfDoc || target < 1 || target > numPages) return;
    const page = await pdfDoc.getPage(target);
    const vp = page.getViewport({ scale: 1 });
    const ps = { width: vp.width, height: vp.height, viewport: vp };
    const s = Math.min(EDITOR_TARGET_W / ps.width, EDITOR_TARGET_H / ps.height, 1.5);

    setCurrentPage(target);
    setPageInputVal(String(target));
    setPageSize(ps);
    scaleRef.current = s;
    setScale(s);
    setSelectedId(null);
  }, [pdfDoc, numPages]);

  // ─── overlay drawing ───
  useEffect(() => {
    const canvas = overlayRef.current;
    if (!canvas || !pageSize) return;

    const dpr = window.devicePixelRatio || 1;
    const newW = Math.round(pageDisplayW * dpr);
    const newH = Math.round(pageDisplayH * dpr);
    if (canvas.width !== newW || canvas.height !== newH) {
      canvas.width = newW;
      canvas.height = newH;
      canvas.style.width = `${pageDisplayW}px`;
      canvas.style.height = `${pageDisplayH}px`;
    }

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, pageDisplayW, pageDisplayH);

    for (const mark of currentMarks) {
      const rect = pdfToDisplay(mark);
      const isSelected = mark.id === selectedId;

      // Semi-transparent red fill
      ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

      // Red border
      ctx.strokeStyle = isSelected ? '#dc2626' : '#ef4444';
      ctx.lineWidth = isSelected ? 2 : 1.5;
      ctx.setLineDash([]);
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);

      // Label
      ctx.fillStyle = 'rgba(220, 38, 38, 0.85)';
      const labelH = 16;
      const labelW = 20;
      ctx.fillRect(rect.x, rect.y - labelH, labelW, labelH);
      ctx.fillStyle = '#fff';
      ctx.font = '10px sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText('R', rect.x + 6, rect.y - labelH / 2);

      // Selection handles — shown on selected mark in ANY tool mode
      if (isSelected) {
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = '#dc2626';
        ctx.lineWidth = 1.5;
        for (const h of getHandlePositions(rect)) {
          ctx.fillRect(h.x - HANDLE_SIZE / 2, h.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
          ctx.strokeRect(h.x - HANDLE_SIZE / 2, h.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
        }

        // Delete button (top-right)
        const btnX = rect.x + rect.w + 4;
        const btnY = rect.y - 4;
        ctx.fillStyle = '#dc2626';
        ctx.beginPath();
        ctx.arc(btnX + 8, btnY + 8, 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(btnX + 4, btnY + 4);
        ctx.lineTo(btnX + 12, btnY + 12);
        ctx.moveTo(btnX + 12, btnY + 4);
        ctx.lineTo(btnX + 4, btnY + 12);
        ctx.stroke();
      }
    }
  }, [currentMarks, selectedId, pageSize, scale, pageDisplayW, pageDisplayH, pdfToDisplay]);

  // ─── shared: try to hit-test selected mark's controls ───
  // Returns true if the click was consumed by a selection interaction
  const trySelectInteraction = useCallback((mx, my) => {
    // 1. Delete button on selected mark
    if (selectedId) {
      const sel = marks.find(m => m.id === selectedId);
      if (sel) {
        const rect = pdfToDisplay(sel);
        const btnCx = rect.x + rect.w + 4 + 8;
        const btnCy = rect.y - 4 + 8;
        if (Math.hypot(mx - btnCx, my - btnCy) <= 12) {
          pushHistory();
          setMarks(prev => prev.filter(m => m.id !== selectedId));
          setSelectedId(null);
          return true;
        }
      }
    }

    // 2. Resize handles on selected mark
    if (selectedId) {
      const sel = marks.find(m => m.id === selectedId);
      if (sel) {
        const rect = pdfToDisplay(sel);
        const handle = getHandleAt(mx, my, rect);
        if (handle) {
          pushHistory();
          dragRef.current = { mode: 'resize', handle, startX: mx, startY: my, startRect: { ...rect }, markId: selectedId };
          return true;
        }
      }
    }

    // 3. Click inside any mark → select + start move
    for (let i = currentMarks.length - 1; i >= 0; i--) {
      const rect = pdfToDisplay(currentMarks[i]);
      if (isInsideRect(mx, my, rect)) {
        const markId = currentMarks[i].id;
        setSelectedId(markId);
        pushHistory();
        dragRef.current = { mode: 'move', startX: mx, startY: my, startRect: { ...rect }, markId };
        return true;
      }
    }

    return false; // nothing hit
  }, [selectedId, marks, currentMarks, pdfToDisplay, pushHistory]);

  // ─── mouse handlers ───
  const handleMouseDown = useCallback((e) => {
    if (!pageSize) return;
    const { offsetX: mx, offsetY: my } = e.nativeEvent;

    // First try to interact with existing marks (select, move, resize, delete)
    if (trySelectInteraction(mx, my)) return;

    // Clicked empty space — deselect any selected mark and start drawing a new rectangle
    setSelectedId(null);
    dragRef.current = { mode: 'draw', startX: mx, startY: my, tempRect: null };
  }, [pageSize, trySelectInteraction]);

  const handleMouseMove = useCallback((e) => {
    const canvas = overlayRef.current;
    if (!canvas || !pageSize) return;
    const { offsetX: mx, offsetY: my } = e.nativeEvent;

    // Update cursor based on what's under the mouse
    const drag = dragRef.current;
    if (drag && drag.mode === 'draw') {
      canvas.style.cursor = 'crosshair';
    } else {
      let cursor = 'crosshair';

      // Check selected mark's handles first
      if (selectedId) {
        const sel = marks.find(m => m.id === selectedId);
        if (sel) {
          const rect = pdfToDisplay(sel);
          const handle = getHandleAt(mx, my, rect);
          if (handle) cursor = HANDLE_CURSORS[handle];
          else if (isInsideRect(mx, my, rect)) cursor = 'move';
        }
      }
      // Check any mark hover (for move cursor)
      if (cursor === 'crosshair') {
        for (const mark of currentMarks) {
          if (isInsideRect(mx, my, pdfToDisplay(mark))) { cursor = 'move'; break; }
        }
      }
      canvas.style.cursor = cursor;
    }

    if (!drag) return;

    const dx = mx - drag.startX;
    const dy = my - drag.startY;

    if (drag.mode === 'draw') {
      const x = Math.max(0, Math.min(drag.startX, mx));
      const y = Math.max(0, Math.min(drag.startY, my));
      const w = Math.min(Math.abs(dx), pageDisplayW - x);
      const h = Math.min(Math.abs(dy), pageDisplayH - y);
      drag.tempRect = { x, y, w, h };

      // Redraw overlay with temp rect
      const dpr = window.devicePixelRatio || 1;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, pageDisplayW, pageDisplayH);

      for (const mark of currentMarks) {
        const rect = pdfToDisplay(mark);
        ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
        ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
        ctx.strokeStyle = '#ef4444';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
      }

      if (w > 2 && h > 2) {
        ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = '#dc2626';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(x, y, w, h);
        ctx.setLineDash([]);
      }
    } else if (drag.mode === 'move') {
      const sr = drag.startRect;
      const newX = Math.max(0, Math.min(sr.x + dx, pageDisplayW - sr.w));
      const newY = Math.max(0, Math.min(sr.y + dy, pageDisplayH - sr.h));
      const pdfCoords = displayToPdf(newX, newY, sr.w, sr.h);
      setMarks(prev => prev.map(m =>
        m.id === drag.markId ? { ...m, ...pdfCoords } : m
      ));
    } else if (drag.mode === 'resize') {
      const newRect = applyResize(drag.handle, drag.startRect, dx, dy);
      newRect.x = Math.max(0, newRect.x);
      newRect.y = Math.max(0, newRect.y);
      newRect.w = Math.min(newRect.w, pageDisplayW - newRect.x);
      newRect.h = Math.min(newRect.h, pageDisplayH - newRect.y);
      if (newRect.w >= MIN_RECT && newRect.h >= MIN_RECT) {
        const pdfCoords = displayToPdf(newRect.x, newRect.y, newRect.w, newRect.h);
        setMarks(prev => prev.map(m =>
          m.id === drag.markId ? { ...m, ...pdfCoords } : m
        ));
      }
    }
  }, [pageSize, selectedId, marks, currentMarks, pageDisplayW, pageDisplayH, pdfToDisplay, displayToPdf]);

  const handleMouseUp = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;

    if (drag.mode === 'draw' && drag.tempRect) {
      const { x, y, w, h } = drag.tempRect;
      if (w >= MIN_RECT && h >= MIN_RECT) {
        pushHistory();
        const pdfCoords = displayToPdf(x, y, w, h);
        const id = genId();
        const newMark = { id, pageNumber: currentPage, ...pdfCoords };
        setMarks(prev => [...prev, newMark]);
        // Auto-select the newly drawn mark so handles appear immediately
        setSelectedId(id);
      }
    }
  }, [currentPage, displayToPdf, pushHistory]);

  // ─── keyboard shortcuts ───
  useEffect(() => {
    const handler = (e) => {
      // Don't capture shortcuts when typing in an input
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      const mod = e.metaKey || e.ctrlKey;

      // Undo: Ctrl/Cmd+Z
      if (mod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      // Redo: Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y
      if ((mod && e.key === 'z' && e.shiftKey) || (mod && e.key === 'y')) {
        e.preventDefault();
        redo();
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedId && !showConfirm) {
          e.preventDefault();
          pushHistory();
          setMarks(prev => prev.filter(m => m.id !== selectedId));
          setSelectedId(null);
        }
      }
      if (e.key === 'Escape') {
        setSelectedId(null);
        if (showConfirm) { setShowConfirm(false); setConfirmInput(''); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedId, showConfirm, undo, redo, pushHistory]);

  // ─── clear all ───
  const handleClearAll = useCallback(() => {
    if (!window.confirm(`Remove all ${totalMarkCount} redaction marks?`)) return;
    pushHistory();
    setMarks([]);
    setSelectedId(null);
  }, [totalMarkCount, pushHistory]);

  // ─── apply redactions ───
  const handleApply = useCallback(async () => {
    if (confirmInput !== 'REDACT') return;

    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      setShowConfirm(false);
      setConfirmInput('');
      return;
    }

    setShowConfirm(false);
    setConfirmInput('');
    setProcessing(true);
    setProgress(0);
    setError(null);

    const rgb = fillColor === 'white' ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 };

    try {
      const bytes = await saveViaWorker({ arrayBuffer, options: { contentRemoval: { targets: marks, redact: true, fillColor: rgb } }, onProgress: setProgress });
      const blob = new Blob([bytes], { type: 'application/pdf' });
      setResult({ blob, size: bytes.length });
      // Redaction starts a new copy. A later Save must never inherit the
      // original document's disk path or cloud ID and silently overwrite it.
      workspace?.remember({ file: new File([blob], `redacted-${fileName.replace(/\.pdf$/i, '')}.pdf`, { type: 'application/pdf' }), dirty: true, workingCopy: true });
      if (usageCapApplies()) incrementUsage();
      setProgress(1);
    } catch (err) {
      setError(`Redaction failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  }, [confirmInput, fillColor, arrayBuffer, marks, workspace, fileName]);

  // ─── result actions ───
  const handleDownload = useCallback(async () => {
    if (!result) return;
    const baseName = fileName.replace(/\.pdf$/i, '');
    try {
      if (workspace?.document) {
        const saved = await saveDocumentSnapshot(workspace.document);
        if (saved) workspace.remember(saved);
      } else await saveFileDialog(result.blob, `redacted-${baseName}.pdf`);
    }
    catch (error) { setError(`Could not save the redacted copy: ${error.message}`); }
  }, [result, fileName, saveFileDialog, workspace]);

  const openInViewer = useCallback(() => {
    if (!result) return;
    if (workspace?.document) { navigate('/viewer'); return; }
    const baseName = fileName.replace(/\.pdf$/i, '');
    const file = new File([result.blob], `redacted-${baseName}.pdf`, { type: 'application/pdf' });
    navigate('/viewer', { state: { file } });
  }, [result, fileName, navigate, workspace?.document]);

  const registerExporter = workspace?.registerExporter, setToolDirty = workspace?.setToolDirty;
  useEffect(() => {
    setToolDirty?.(Boolean(totalMarkCount && !result));
    return () => setToolDirty?.(false);
  }, [setToolDirty, totalMarkCount, result]);
  useEffect(() => registerExporter?.(() => {
    if (totalMarkCount && !result) throw new Error('Apply your redactions or clear the marks before leaving this tool or saving.');
    return workspace?.document;
  }), [registerExporter, workspace?.document, result, totalMarkCount, fileName]);

  // ─── beforeunload warning ───
  useEffect(() => {
    if (totalMarkCount === 0 || result) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [totalMarkCount, result]);

  // ─── render: upload ───
  if (!pdfDoc) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <h1 className="text-lg font-semibold text-gray-900">Redact PDF</h1>
          <p className="text-sm text-gray-500">Permanently remove sensitive content</p>
        </div>

        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-73px)] p-8">
          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-xl p-4 mb-6 max-w-xl">
            <p>
              Redact permanently destroys the content under your marks — it can't be recovered
              or un-redacted in the saved copy. Other text stays searchable, and unmarked
              graphics and form fields remain intact. Metadata, attachments, bookmarks
              and editing history are removed to avoid retaining hidden copies.
            </p>
          </div>

          <div
            onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
            onClick={handleBrowse}
            className={`w-full max-w-xl border-2 border-dashed rounded-2xl p-16 text-center cursor-pointer transition-all ${
              isDragging ? 'border-brand-blue bg-brand-light/30 scale-[1.02]' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
            }`}
          >
            <Upload size={40} className={`mx-auto mb-4 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
            <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
            <p className="text-sm text-gray-400 mt-1">PDF files only</p>
          </div>
          <CloudSourceHint
            onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
            className="mt-4 w-full max-w-xl"
          />
          {error && <p role="alert" className="mt-4 text-red-600 text-sm">{error}</p>}
        </div>
      </div>
    );
  }

  // ─── render: result ───
  if (result) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <h1 className="text-lg font-semibold text-gray-900">Redact PDF</h1>
          <p className="text-sm text-gray-500">Redaction complete</p>
        </div>

        <div className="max-w-lg mx-auto p-6 mt-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-center gap-3 mb-4">
              <ShieldCheck size={32} className="text-green-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 text-center mb-1">Redaction Applied</h2>
            <p className="text-sm text-gray-500 text-center mb-6">
              {totalMarkCount} area{totalMarkCount !== 1 ? 's' : ''} redacted across {pagesWithMarks} page{pagesWithMarks !== 1 ? 's' : ''}
            </p>

            <div className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 rounded-lg p-3 mb-6">
              <div>
                <p className="text-xs text-gray-400">Original</p>
                <p className="font-medium">{formatFileSize(fileSize)}</p>
              </div>
              <span className="text-gray-300">&rarr;</span>
              <div className="text-right">
                <p className="text-xs text-gray-400">Redacted</p>
                <p className="font-medium">{formatFileSize(result.size)}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={openInViewer}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
              >
                <Eye size={16} />
                Open in Viewer
              </button>
              <button
                onClick={handleDownload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                <Download size={16} />
                {window.electronAPI ? 'Save copy' : 'Download'}
              </button>
            </div>
            {error && <p role="alert" className="mt-4 text-sm text-red-700">{error}</p>}
          </div>
        </div>
      </div>
    );
  }

  // ─── render: gated / editor ───
  if (redactGated) {
    return <ProGate feature="redact"><div /></ProGate>;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4 shrink-0">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">Redact PDF</h1>
          <p className="text-sm text-gray-500 truncate">{fileName}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleBrowse}
            className="text-sm text-brand-blue hover:underline font-medium"
          >
            Change file
          </button>
        </div>
      </div>

      {/* Pending redaction warning */}
      {totalMarkCount > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center gap-2 shrink-0">
          <AlertTriangle size={16} className="text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            <span className="font-medium">{totalMarkCount} redaction mark{totalMarkCount !== 1 ? 's' : ''} pending.</span>
            {' '}Content is NOT yet removed until you click Apply Redactions.
          </p>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center gap-3 shrink-0 flex-wrap">
        {/* Undo / Redo */}
        <div className="flex items-center gap-1">
          <button
            onClick={undo}
            disabled={history.length === 0}
            title="Undo (Ctrl+Z)"
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Undo2 size={16} className="text-gray-600" />
          </button>
          <button
            onClick={redo}
            disabled={future.length === 0}
            title="Redo (Ctrl+Shift+Z)"
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Redo2 size={16} className="text-gray-600" />
          </button>
        </div>

        {/* Separator */}
        <div className="w-px h-6 bg-gray-200" />

        {/* Fill color */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Fill:</span>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setFillColor('black')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                fillColor === 'black' ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className="w-3 h-3 rounded-sm bg-black border border-gray-400 inline-block" />
              Black
            </button>
            <button
              onClick={() => setFillColor('white')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                fillColor === 'white' ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className="w-3 h-3 rounded-sm bg-white border border-gray-400 inline-block" />
              White
            </button>
          </div>
        </div>

        {/* Separator */}
        <div className="w-px h-6 bg-gray-200" />

        {/* Mark count */}
        <span className="text-sm text-gray-500">
          {totalMarkCount === 0 ? 'No areas marked' : `${totalMarkCount} area${totalMarkCount !== 1 ? 's' : ''} marked`}
        </span>

        {/* Clear all */}
        {totalMarkCount > 0 && (
          <button
            onClick={handleClearAll}
            className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium ml-1"
          >
            <Trash2 size={14} />
            Clear All
          </button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Page navigation */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => goToPage(currentPage - 1)}
            aria-label="Previous page"
            disabled={currentPage <= 1}
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={18} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-1 text-sm">
            <input
              type="text"
              aria-label="Page number"
              value={pageInputVal}
              onChange={(e) => setPageInputVal(e.target.value)}
              onBlur={() => {
                const n = parseInt(pageInputVal, 10);
                if (n >= 1 && n <= numPages) goToPage(n);
                else setPageInputVal(String(currentPage));
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              className="w-10 text-center border border-gray-200 rounded py-0.5 text-sm"
            />
            <span className="text-gray-400">/ {numPages}</span>
          </div>
          <button
            onClick={() => goToPage(currentPage + 1)}
            aria-label="Next page"
            disabled={currentPage >= numPages}
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={18} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Main editor area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Page view */}
        <div className="flex-1 flex items-start justify-center overflow-auto p-6 bg-gray-100">
          <div className="relative shadow-lg" style={{ width: pageDisplayW, height: pageDisplayH }}>
            <PageCanvas pdfDoc={pdfDoc} pageNumber={currentPage} scale={scale} />
            <canvas
              ref={overlayRef}
              aria-label="Mark redaction areas"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={() => { dragRef.current = null; }}
              className="absolute inset-0"
              style={{ cursor: 'crosshair' }}
            />
          </div>
        </div>

        {/* Right sidebar */}
        <div className="w-72 bg-white border-l border-gray-200 p-4 flex flex-col shrink-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Pages</p>
          <div className="flex-1 overflow-y-auto mb-4 space-y-1.5 min-h-0">
            {Array.from({ length: numPages }, (_, i) => i + 1).map(pg => {
              const pgMarks = marks.filter(m => m.pageNumber === pg).length;
              const isCurrent = pg === currentPage;
              return (
                <button
                  key={pg}
                  onClick={() => goToPage(pg)}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                    isCurrent ? 'bg-brand-light text-brand-blue font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex-1 text-left">Page {pg}</span>
                  {pgMarks > 0 && (
                    <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">
                      {pgMarks}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="border-t border-gray-200 pt-4 space-y-3">
            {error && <p className="text-red-600 text-sm">{error}</p>}

            {processing && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 size={16} className="animate-spin text-brand-blue" />
                  <span className="text-sm text-gray-600">
                    Applying redactions... {Math.round(progress * 100)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-500 rounded-full transition-all duration-200"
                    style={{ width: `${progress * 100}%` }}
                  />
                </div>
              </div>
            )}

            <button
              onClick={() => { setShowConfirm(true); setConfirmInput(''); }}
              disabled={totalMarkCount === 0 || processing}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <EyeOff size={18} />
              Apply Redactions
            </button>

            {totalMarkCount > 0 && !processing && (
              <p className="text-xs text-gray-400 text-center">
                {totalMarkCount} area{totalMarkCount !== 1 ? 's' : ''} on {pagesWithMarks} page{pagesWithMarks !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Apply Redactions?</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              This will permanently destroy the content under <strong>{totalMarkCount} redaction area{totalMarkCount !== 1 ? 's' : ''}</strong> across{' '}
              <strong>{pagesWithMarks} page{pagesWithMarks !== 1 ? 's' : ''}</strong>. <strong>This cannot be undone.</strong>
            </p>
            <ul className="text-xs text-gray-500 mb-4 list-disc pl-5 space-y-1">
              <li>Marked text and image regions are removed without converting pages to images.</li>
              <li>Overlapping graphics, links, annotations and form fields are removed. Other content stays usable.</li>
              <li>Metadata, attachments, bookmarks and editing history are removed from the copy.</li>
            </ul>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Type <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-red-600">REDACT</span> to confirm
              </label>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && confirmInput === 'REDACT') handleApply(); }}
                placeholder="REDACT"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setShowConfirm(false); setConfirmInput(''); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                disabled={confirmInput !== 'REDACT'}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <EyeOff size={16} />
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
  );
}
